import axios from 'axios'

// Get API configuration
// CRITICAL: This instance is used by stores/components for regular API calls
// Final URL construction: baseURL/v1/admin + path
// Dev: https://api.banjinos.dev/v1/admin + /vendors
// Prod: https://api.banjinos.com/v1/admin + /vendors
const baseURL = import.meta.env.VITE_API_URL || (window.location.hostname.includes('.com') ? 'https://api.banjinos.com/api' : 'https://api.banjinos.dev/api')
const apiVersion = 'v1'
const apiGateway = 'admin'

const apiClient = axios.create({
  baseURL: `${baseURL}/${apiVersion}/${apiGateway}`,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // If the data is FormData, let axios handle the Content-Type
    // It will automatically set the boundary parameter
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }
    
    return config
  },
  error => Promise.reject(error)
)

// Handle responses
apiClient.interceptors.response.use(
  response => response,
  error => {
    console.error('API Error:', error)
    
    // Handle token expiration
    if (error.response?.status === 401) {
      if (error.response.data?.code === 'TOKEN_EXPIRED' || 
          error.response.data?.error === 'Invalid authentication') {
        // Clear local storage
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('client')
        
        // Redirect to login
        window.location.href = '/login'
      }
    }
    
    return Promise.reject(error)
  }
)

export default {
  // Vendors
  vendors: {
    getAll: () => apiClient.get('/vendors'),
    getById: (id) => apiClient.get(`/vendors/${id}`),
    create: (data) => apiClient.post('/vendors', data),
    update: (id, data) => apiClient.put(`/vendors/${id}`, data),
    delete: (id) => apiClient.delete(`/vendors/${id}`),
    safeDelete: (id) => apiClient.delete(`/vendors-safe/${id}/safe`),
    forceDelete: (id) => apiClient.delete(`/vendors-safe/${id}/force`)
  },
  
  // Materials (formerly Ingredients)
  materials: {
    getAll: () => apiClient.get('/materials?limit=100'),
    getById: (id) => apiClient.get(`/materials/${id}`),
    create: (data) => apiClient.post('/materials', data),
    update: (id, data) => apiClient.put(`/materials/${id}`, data),
    delete: (id) => apiClient.delete(`/materials/${id}`),
    
    // Pricing Tiers
    createPricingTier: (materialId, data) => 
      apiClient.post(`/materials/${materialId}/pricing-tiers`, data),
    updatePricingTier: (materialId, tierId, data) => 
      apiClient.put(`/materials/${materialId}/pricing-tiers/${tierId}`, data),
    deletePricingTier: (materialId, tierId) => 
      apiClient.delete(`/materials/${materialId}/pricing-tiers/${tierId}`)
  },
  
  // Recipes
  recipes: {
    getAll: () => apiClient.get('/recipes'),
    getById: (id) => apiClient.get(`/recipes/${id}`),
    create: (data) => apiClient.post('/recipes', data),
    update: (id, data) => apiClient.put(`/recipes/${id}`, data),
    delete: (id) => apiClient.delete(`/recipes/${id}`),
    getScenarios: (id) => apiClient.get(`/recipes/${id}/scenarios`),
    getSimpleBatchCost: (id, params) => apiClient.get(`/recipes/${id}/simple-batch-cost`, { params })
  },
  
  // Production Scenarios
  scenarios: {
    getAll: () => apiClient.get('/scenarios'),
    getById: (id) => apiClient.get(`/scenarios/${id}`),
    create: (data) => apiClient.post('/scenarios', data),
    update: (id, data) => apiClient.put(`/scenarios/${id}`, data),
    delete: (id) => apiClient.delete(`/scenarios/${id}`),
    getCosts: (id) => apiClient.get(`/scenarios/${id}/costs`)
  },
  
  // Cost Calculations
  costs: {
    calculate: (scenarioId) => apiClient.post(`/costs/calculate/${scenarioId}`),
    save: (scenarioId) => apiClient.post(`/costs/save/${scenarioId}`),
    compareByRecipe: (recipeId) => apiClient.get(`/costs/compare/recipe/${recipeId}`)
  },
  
  // Batches
  batches: {
    getCosts: (id) => apiClient.get(`/operations/batches/${id}/costs`),
    recordCosts: (id, data) => apiClient.patch(`/operations/batches/${id}/costs`, data),
    recordMaterialReceipt: (data) => apiClient.post('/operations/material-lots/receipts', data),
    getMaterialLots: (materialId) => apiClient.get('/operations/material-lots', { params: { material_id: materialId } }),
    getVendorInvoices: (params = {}) => apiClient.get('/operations/vendor-invoices', { params }),
    recordVendorInvoice: (data) => apiClient.post('/operations/vendor-invoices', data),
    linkInvoiceMaterial: (data) => apiClient.post('/operations/vendor-invoices/link-material', data),
    getAll: () => apiClient.get('/batches'),
    getById: (id) => apiClient.get(`/batches/${id}`),
    create: (data) => apiClient.post('/batches', data),
    update: (id, data) => apiClient.put(`/batches/${id}`, data),
    delete: (id) => apiClient.delete(`/batches/${id}`),
    recalculateMaterials: (id) => apiClient.post(`/batches/${id}/recalculate-materials`),
    updateVendorAssignments: (batchId, assignments) => apiClient.put(`/batches/${batchId}/materials/vendor-assignments`, { assignments }),
    // Excise Tax
    getExciseTaxRates: () => apiClient.get('/batches/excise-tax-rates'),
    getExciseTaxClasses: () => apiClient.get('/batches/excise-tax-classes'),
    getExciseTaxYTDSummary: (year = null) => apiClient.get('/batches/excise-tax-ytd-summary', { params: { year } }),
    // Phase 3: Batch Material Consumption Tracking
    getConsumptionSummary: (batchId) => apiClient.get(`/batches/${batchId}/consumption-summary`),
    getMaterialConsumptions: (batchId, materialId) => apiClient.get(`/batches/${batchId}/materials/${materialId}/consumptions`),
    recordMaterialConsumption: (batchId, materialId, data) => apiClient.post(`/batches/${batchId}/materials/${materialId}/consumptions`, data)
  },
  
  // Ingredients (alias for materials for backward compatibility)
  ingredients: {
    getAll: () => apiClient.get('/materials'),
    getById: (id) => apiClient.get(`/materials/${id}`),
    create: (data) => apiClient.post('/materials', data),
    update: (id, data) => apiClient.put(`/materials/${id}`, data),
    delete: (id) => apiClient.delete(`/materials/${id}`)
  },
  
  // Content Management
  content: {
    getAll: () => apiClient.get('/content'),
    getById: (id) => apiClient.get(`/content/${id}`),
    create: (data) => apiClient.post('/content', data),
    update: (id, data) => apiClient.put(`/content/${id}`, data),
    delete: (id) => apiClient.delete(`/content/${id}`),
    feature: (id, featured) => apiClient.patch(`/content/${id}/feature`, { featured }),
    
    // Enhanced Provenance System
    getProvenance: (id) => apiClient.get(`/content/${id}/provenance`),
    getPackages: (id) => apiClient.get(`/content/${id}/packages`),
    getActions: (id) => apiClient.get(`/content/${id}/actions`),
    restoreVersion: (id, data) => apiClient.post(`/content/${id}/restore-version`, data),
    
    // Version Reconstruction
    getReconstructionCapabilities: (id) => apiClient.get(`/content/${id}/reconstruction/capabilities`),
    validateReconstruction: (id, data) => apiClient.post(`/content/${id}/reconstruction/validate`, data),
    previewReconstruction: (id, data) => apiClient.post(`/content/${id}/reconstruction/preview`, data)
  },

  // SVG Management (legacy - will be deprecated)
  svg: {
    getIllustrations: () => apiClient.get('/svg/illustrations'),
    syncIllustrations: () => apiClient.post('/svg/illustrations/sync'),
    assignToContent: (contentId, data) => apiClient.post(`/svg/content/${contentId}/assign-svg`, data),
    getAssignments: () => apiClient.get('/svg/assignments'),
    removeAssignment: (contentId) => apiClient.delete(`/svg/content/${contentId}/assign-svg`)
  },

  // Illustrations Management (supports all image types)
  illustrations: {
    getAll: (query = {}) => {
      const params = new URLSearchParams(query);
      return apiClient.get(`/illustrations?${params}`);
    },
    sync: () => apiClient.post('/illustrations/sync'),
    upload: (formData) => apiClient.post('/illustrations/upload', formData),
    assignToContent: (contentId, data) => apiClient.post(`/illustrations/content/${contentId}/assign`, data),
    removeFromContent: (contentId) => apiClient.delete(`/illustrations/content/${contentId}/assign`),
    delete: (illustrationId, options = {}) => {
      const params = new URLSearchParams(options);
      return apiClient.delete(`/illustrations/${illustrationId}?${params}`);
    },
    getContentMappings: () => apiClient.get('/illustrations/content-mappings')
  },

  // QR Redirects
  qrRedirects: {
    getAll: (params = {}) => apiClient.get('/qr-redirects', { params }),
    getById: (code) => apiClient.get(`/qr-redirects/${code}`),
    getAnalytics: (code, days = 30) => apiClient.get(`/qr-redirects/${code}/analytics?days=${days}`),
    create: (data) => apiClient.post('/qr-redirects', data),
    update: (code, data) => apiClient.patch(`/qr-redirects/${code}`, data),
    delete: (code) => apiClient.delete(`/qr-redirects/${code}`),
    invalidateCache: (code) => apiClient.post(`/qr-redirects/${code}/invalidate-cache`)
  },

  // The audience registry. Read-only: an audience is created by marking a QR
  // code with it, because ?aud= is a public parameter and minting one must be
  // an operator act. See docs/features/QR_AUDIENCES.md.
  audiences: {
    getAll: () => apiClient.get('/audiences')
  },

  // Newsletter subscribers (read-only here: consent is evidenced by the person
  // subscribing themselves, so there is no add-subscriber call anywhere).
  newsletter: {
    getFanBonusClaims: (params = {}) => apiClient.get('/newsletter/fan-bonus', { params }),
    retryFanBonusEmails: id => apiClient.post(`/newsletter/fan-bonus/${id}/retry`),
    getSubscribers: (params = {}) => apiClient.get('/newsletter/subscribers', { params }),
    getSubscriberStats: () => apiClient.get('/newsletter/subscribers/stats')
  },

  // Documents
  documents: {
    getAll: (params = {}) => {
      const query = new URLSearchParams(params)
      return apiClient.get(`/documents?${query}`)
    },
    getById: (id) => apiClient.get(`/documents/${id}`),
    upload: (formData) => apiClient.post('/documents', formData),
    update: (id, data) => apiClient.put(`/documents/${id}`, data),
    delete: (id) => apiClient.delete(`/documents/${id}`),
    getDownloadUrl: (id) => apiClient.get(`/documents/${id}/download-url`),
    addLinks: (id, data) => apiClient.post(`/documents/${id}/links`, data),
    removeLinks: (id, data) => apiClient.delete(`/documents/${id}/links`, { data })
  },

  // Vendor Invoices (Cost Accounting Phase 1)
  vendorInvoices: {
    getAll: (params = {}) => {
      const query = new URLSearchParams(params)
      return apiClient.get(`/vendor-invoices?${query}`)
    },
    getById: (id) => apiClient.get(`/vendor-invoices/${id}`),
    create: (data) => apiClient.post('/vendor-invoices', data),
    createFromPO: (poId, data) => apiClient.post(`/vendor-invoices/from-po/${poId}`, data),
    update: (id, data) => apiClient.put(`/vendor-invoices/${id}`, data),
    delete: (id) => apiClient.delete(`/vendor-invoices/${id}`),
    addLineItem: (id, data) => apiClient.post(`/vendor-invoices/${id}/line-items`, data),
    updateLineItem: (id, lineId, data) => apiClient.put(`/vendor-invoices/${id}/line-items/${lineId}`, data),
    deleteLineItem: (id, lineId) => apiClient.delete(`/vendor-invoices/${id}/line-items/${lineId}`),
    getPayables: () => apiClient.get('/vendor-invoices/payables'),
    getAging: () => apiClient.get('/vendor-invoices/aging')
  },

  // Payments (Cost Accounting Phase 1)
  payments: {
    getAll: (params = {}) => {
      const query = new URLSearchParams(params)
      return apiClient.get(`/payments?${query}`)
    },
    getById: (id) => apiClient.get(`/payments/${id}`),
    create: (data) => apiClient.post('/payments', data),
    update: (id, data) => apiClient.put(`/payments/${id}`, data),
    delete: (id) => apiClient.delete(`/payments/${id}`),
    addAllocation: (id, data) => apiClient.post(`/payments/${id}/allocate`, data),
    removeAllocation: (id, allocId) => apiClient.delete(`/payments/${id}/allocations/${allocId}`),
    getOutstandingInvoices: (vendorId) => apiClient.get(`/payments/vendor/${vendorId}/outstanding`)
  },

  // Material Inventory (Phase 2)
  inventory: {
    getAll: () => apiClient.get('/inventory'),
    getByMaterial: (materialId) => apiClient.get(`/inventory/material/${materialId}`),
    receive: (data) => apiClient.post('/inventory/receive', data),
    update: (id, data) => apiClient.put(`/inventory/${id}`, data),
    adjust: (data) => apiClient.post('/inventory/adjust', data),
    linkToInvoice: (inventoryId, data) => apiClient.post(`/inventory/${inventoryId}/link-invoice`, data),
    getReorderNeeded: () => apiClient.get('/inventory/reorder-needed')
  },

  // Units
  units: {
    getAll: () => apiClient.get('/units'),
    getByCategory: (category) => apiClient.get(`/units/category/${category}`),
  },

  music: {
    getAll: (query = {}) => {
      const params = new URLSearchParams(query);
      return apiClient.get(`/music?${params}`);
    },
    sync: () => apiClient.post('/music/sync'),
    upload: (formData) => apiClient.post('/music/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (musicId) => apiClient.delete(`/music/${musicId}`),
    getById: (musicId) => apiClient.get(`/music/${musicId}`)
  },

  // Direct access to axios methods for backward compatibility
  get: (url, config) => apiClient.get(url, config),
  post: (url, data, config) => apiClient.post(url, data, config),
  put: (url, data, config) => apiClient.put(url, data, config),
  patch: (url, data, config) => apiClient.patch(url, data, config),
  delete: (url, config) => apiClient.delete(url, config)
}
